import LELogger from './util/logger';
import { submitLead } from './submit_lead';
import { sendLeadFormHeapEvents } from './form_analytics';
import { EventNameTypes, makeSegmentTrackCall } from './analytics';
import { varsFromServer } from './client_side_injection_vars';

// List of drift supported langauges https://help.drift.com/article/custom-language/
// Passing an unsupported language will break drift.
const driftLanguages = [
  'en',
  'es',
  'de',
  'de-DE',
  'de-DE-formal',
  'hi',
  'hu',
  'et',
  'is',
  'nl',
  'pt',
  'pt-PT',
  'pt-BR',
  'sv',
  'zh',
  'fr',
  'it',
  'ru',
  'pl',
  'sl',
  'da',
  'fi',
  'hr',
  'ko',
  'nb',
  'ro',
  'sr',
  'th',
  'vi'
];

export const loadDrift = () => {
  // From drift.js snippet (DO NOT MODIFY)
  /* eslint-disable */
  !(function() {
    var t;
    if (((t = window.driftt = window.drift = window.driftt || []), !t.init))
      return t.invoked
        ? void (window.console && console.error && console.error('Drift snippet included twice.'))
        : ((t.invoked = !0),
          (t.methods = ['identify', 'config', 'track', 'reset', 'debug', 'show', 'ping', 'page', 'hide', 'off', 'on']),
          (t.factory = function(e) {
            return function() {
              var n;
              return (n = Array.prototype.slice.call(arguments)), n.unshift(e), t.push(n), t;
            };
          }),
          t.methods.forEach(function(e) {
            t[e] = t.factory(e);
          }),
          (t.load = function(t) {
            var e, n, o, i;
            (e = 3e5),
              (i = Math.ceil(new Date() / e) * e),
              (o = document.createElement('script')),
              (o.type = 'text/javascript'),
              (o.async = !0),
              (o.crossorigin = 'anonymous'),
              (o.src = 'https://js.driftt.com/include/' + i + '/' + t + '.js'),
              (n = document.getElementsByTagName('script')[0]),
              n.parentNode.insertBefore(o, n);
          }));
  })();
  /* eslint-enable */
};

export function initializeDrift() {
  loadDrift();
  // Set drift language based on locale, default to English
  const { locale } = varsFromServer;
  const language = locale.split('-')[0];
  let driftLocale = 'en';
  if (driftLanguages.includes(locale)) {
    driftLocale = locale;
  } else if (driftLanguages.includes(language)) {
    driftLocale = language;
  }

  global.drift.config({ locale: driftLocale });

  global.drift.load(varsFromServer.driftChatKey);

  global.drift.debug(varsFromServer.driftDebug);

  bindDriftEventHandlers();
}

const leadSourceDetail = 'Drift Email';
let emailSubmitted = false;

const log = {
  debug() {}
};

function trackChatActivity(data) {
  makeSegmentTrackCall(EventNameTypes.chatActivity, data);
}

function driftOnWelcomeMessageOpen() {
  log.debug('Welcome Message - Opened!');
  trackChatActivity({
    label: 'Welcome Message - Opened'
  });
}

function driftOnWelcomeMessageClose() {
  log.debug('Welcome Message - Closed!');
  trackChatActivity({
    label: 'Welcome Message - Closed'
  });
}

function driftOnAwayMessageOpen() {
  log.debug('Away Message - Opened!');
  trackChatActivity({
    label: 'Away Message - Opened'
  });
}

function driftOnAwayMessageClose() {
  log.debug('Away Message - Closed!');
  trackChatActivity({
    label: 'Away Message - Closed'
  });
}

function driftOnCampaignOpen({ campaignId }) {
  log.debug('Campaign - Opened!');
  trackChatActivity({
    label: 'Campaign - Opened',
    campaignId
  });
}

function driftOnCampaignDismiss(event) {
  log.debug('Campaign - Dismissed!');
  trackChatActivity({
    label: 'Campaign - Dismissed',
    ...event.data
  });
}

function driftOnCampaignClick(event) {
  log.debug('Campaign - CTA Clicked!');
  trackChatActivity({
    label: 'Campaign - CTA Clicked',
    ...event.data
  });
}

function driftOnCampaignSubmit(event) {
  log.debug('Campaign - Submit!');
  trackChatActivity({
    label: 'Campaign - Submit',
    ...event.data
  });
}

function driftOnStartConversation(event) {
  log.debug('Started a new conversation!', event);
  trackChatActivity({
    label: 'Start Conversation',
    ...event.data
  });
}

function driftOnSidebarOpen(event) {
  trackChatActivity({
    label: 'Sidebar Open',
    ...event.data
  });
}

function driftOnSidebarClose(event) {
  trackChatActivity({
    label: 'Sidebar Close',
    ...event.data
  });
}

function driftOnMessageSent(event) {
  log.debug('User replied to conversation!', event);
  trackChatActivity({
    label: 'Message Sent',
    ...event.data
  });
}

function driftOnMessage(event) {
  log.debug('User received a message!', event);
  trackChatActivity({
    label: 'Message',
    ...event.data
  });
}

async function driftOnEmailCapture(event) {
  if (!emailSubmitted) {
    try {
      await submitLeadForm(event);
    } catch (err) {
      handleSubmitLeadFormError(err);
    }
  }
}

async function submitLeadForm(event) {
  emailSubmitted = true;
  const formParams = {
    email: event.data.email,
    submission_type: 'email'
  };

  await submitLead(formParams, leadSourceDetail);

  sendLeadFormHeapEvents(formParams, leadSourceDetail);

  log.debug('Email captured and lead submitted!');
}

function handleSubmitLeadFormError(err) {
  emailSubmitted = false;

  const logMsg = {
    ...err,
    message: 'Failed to submit Drift Email Lead'
  };
  // in case it's an Error obj
  if (err.message) {
    logMsg.err = err.message;
  }
  LELogger.error(logMsg);
}

function driftOnSchedulingRequestMeeting(event) {
  log.debug('user wants to schedule a meeting!', event);
  trackChatActivity({
    label: 'Schedule Request',
    ...event.data
  });
}

function driftOnSchedulingMeetingBooked(event) {
  log.debug('User scheduled a meeting!', event);
  trackChatActivity({
    label: 'Schedule Booked',
    ...event.data
  });
}

export function bindDriftEventHandlers() {
  const { drift } = window;

  // Welcome Message Events
  drift.on('welcomeMessage:open', driftOnWelcomeMessageOpen);
  drift.on('welcomeMessage:close', driftOnWelcomeMessageClose);

  // Away Message Events
  drift.on('awayMessage:open', driftOnAwayMessageOpen);
  drift.on('awayMessage:close', driftOnAwayMessageClose);

  // Campaign Events
  drift.on('campaign:open', driftOnCampaignOpen);
  drift.on('campaign:dismiss', driftOnCampaignDismiss);
  drift.on('campaign:click', driftOnCampaignClick);
  drift.on('campaign:submit', driftOnCampaignSubmit);

  // Conversation Events
  drift.on('startConversation', driftOnStartConversation);

  // Sidebar Handling
  drift.on('sidebarOpen', driftOnSidebarOpen);
  drift.on('sidebarClose', driftOnSidebarClose);

  // Message Events
  drift.on('message:sent', driftOnMessageSent);
  drift.on('message', driftOnMessage);

  // Email Capture Events
  drift.on('emailCapture', driftOnEmailCapture);

  // Scheduling Events
  drift.on('scheduling:requestMeeting', driftOnSchedulingRequestMeeting);
  drift.on('scheduling:meetingBooked', driftOnSchedulingMeetingBooked);
}
